import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
  ShieldCheck, Phone, Upload, FileCheck, CheckCircle2,
  AlertTriangle, Loader2, Lock, Camera, BookOpen, Send, Shield
} from 'lucide-react';
import SignaturePad from '../components/SignaturePad';
import { sendSMSWithRateLimit } from '../utils/smsService';

import { validateSLPhone, sanitizePhone } from '../utils/validation';
import { unsealSensitiveDoc } from '../utils/security';

export default function GuarantorVerify() {
  const { loanId } = useParams();
  const [loan, setLoan] = useState(null);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sentOtp, setSentOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [files, setFiles] = useState({ nicFront: null, nicBack: null, bankStatement: null, signature: null });

  useEffect(() => {
    // Fetch Global Config
    getDoc(doc(db, 'configs', 'main')).then(snap => {
      if (snap.exists()) setConfig(snap.data());
    });

    getDoc(doc(db, 'loans', loanId))
      .then(snap => {
        if (snap.exists()) setLoan(unsealSensitiveDoc(snap.data()));
        else setError('Verification link has expired or is invalid.');
      })
      .catch(() => setError('Connection error. Please try again later.'))
      .finally(() => setLoading(false));
  }, [loanId]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!validateSLPhone(phone)) return setError('Please enter a valid Sri Lankan mobile number.');
    const match = loan.details.guarantors.find(g => g.phone === phone);
    if (!match) return setError('This phone number is not registered for this loan.');
    
    setIsVerifying(true);
    setError('');

    try {
      const generated = Math.floor(100000 + Math.random() * 900000).toString();
      const text = `Finova: Your OTP for guarantor verification is ${generated}. Valid for 5 minutes.`;
      
      const res = await sendSMSWithRateLimit(phone, text, {
        apikey: config.smsApiKey || process.env.REACT_APP_SMS_API_KEY,
        apitoken: config.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN,
        from: config.smsFrom || process.env.REACT_APP_SMS_SENDER_ID
      });

      if (res.status === 'queued' || res.status === 'success') {
         setSentOtp(generated);
         setOtpSent(true);
      } else {
        setError('Failed to send OTP: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      setError('SMS service unavailable');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    if (otp !== sentOtp) return setError('Invalid OTP. Please try again.');
    
    setIsVerifying(true);
    setError('');
    try {
      // Check if phone number is blocked
      const q = query(collection(db, 'users'), where('personal.mobileNumber', '==', phone));
      const snap = await getDocs(q);
      let isBlocked = false;
      snap.forEach(d => { if (d.data().isBlocked) isBlocked = true; });
      if (isBlocked) {
        setIsVerifying(false);
        return setError('Your account has been restricted. You cannot act as a guarantor.');
      }
      setStep(2);
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!files.nicFront || !files.nicBack || !files.bankStatement || !files.signature)
      return alert('All documents and your signature are required.');
    try {
      setLoading(true);
      const upload = async (file, label) => {
        const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'YOUR_CLOUD_NAME';
        const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'YOUR_UPLOAD_PRESET';
        
        const formData = new FormData();
        if (typeof file === 'string') {
          const blob = await (await fetch(file)).blob();
          formData.append('file', blob);
        } else {
          formData.append('file', file);
        }
        formData.append('upload_preset', uploadPreset);
        
        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error?.message || 'Cloudinary upload failed');
        }
        return data.secure_url;
      };
      const [nicFrontUrl, nicBackUrl, bankUrl, signatureUrl] = await Promise.all([
        upload(files.nicFront, 'nic_front'),
        upload(files.nicBack, 'nic_back'),
        upload(files.bankStatement, 'payslips'),
        upload(files.signature, 'signature'),
      ]);
      const updatedGuarantors = loan.details.guarantors.map(g =>
        g.phone === phone
          ? { ...g, status: 'verified', nicFrontUrl, nicBackUrl, payslipsUrl: bankUrl, signatureUrl, verifiedAt: new Date().toISOString() }
          : g
      );
      const allVerified = updatedGuarantors.every(g => g.status === 'verified');
      const loanUpdate = {
        'details.guarantors': updatedGuarantors,
        status: allVerified ? 'pending_admin' : 'pending_guarantor',
      };

      if (loan.guarantor?.phone === phone) {
        loanUpdate.guarantor = { 
          ...loan.guarantor, 
          status: 'verified',
          nicFrontUrl, nicBackUrl, payslipsUrl: bankUrl, signatureUrl 
        };
      }

      await updateDoc(doc(db, 'loans', loanId), loanUpdate);
      setStep(3);
    } catch (err) {
      console.error(err);
      alert('Upload failed. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const STEPS = ['Verify', 'Documents', 'Complete'];

  if (loading && step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stripe-bg">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-stripe-border border-t-stripe-blue rounded-full animate-spin" />
          <p className="text-sm font-medium text-stripe-slate">Verifying link…</p>
        </div>
      </div>
    );
  }

  if (error && step === 1 && !loan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stripe-bg p-6">
        <div className="s-card max-w-sm w-full text-center p-8">
          <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-red-200">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-stripe-dark mb-2">Access Denied</h2>
          <p className="text-sm text-stripe-slate mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-dark w-full py-2.5 text-sm">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stripe-bg flex flex-col">
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="flex items-center gap-3">
            <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-7 w-auto object-contain" />
            <div className="h-6 w-px bg-stripe-border mx-1"></div>
            <div>
              <p className="text-sm font-bold text-stripe-dark">Guarantor Verification</p>
              <p className="text-xs text-stripe-light whitespace-nowrap">Secure Portal</p>
            </div>
          </div>
          {/* Step indicator */}
          <div className="flex items-center gap-1">
            {STEPS.map((label, idx) => {
              const i = idx + 1;
              const done = step > i;
              const active = step === i;
              return (
                <div key={i} className="flex items-center gap-1">
                  <div className={`flex items-center gap-1.5 ${active ? 'text-stripe-blue' : done ? 'text-green-600' : 'text-stripe-light'}`}>
                    <div className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center ${
                      done ? 'bg-green-100 text-green-600' :
                      active ? 'bg-stripe-blue text-white' :
                      'bg-stripe-bg text-stripe-light border border-stripe-border'
                    }`}>
                      {done ? '✓' : i}
                    </div>
                    <span className="hidden sm:block text-xs font-medium">{label}</span>
                  </div>
                  {idx < STEPS.length - 1 && <span className="text-stripe-border mx-1">·</span>}
                </div>
              );
            })}
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6 py-8">
        <div className="w-full max-w-lg">

          {/* Step 1: Phone verification */}
          {step === 1 && (
            <div className="s-card p-8 animate-in fade-in duration-300">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary-200">
                  <Phone size={24} className="text-stripe-blue" />
                </div>
                <h2 className="text-xl font-bold text-stripe-dark">Identity Verification</h2>
                <p className="text-sm text-stripe-slate mt-1.5">
                  Enter your mobile number to verify your identity.
                </p>
              </div>

              {loan && (
                <div className="p-4 bg-stripe-bg rounded-xl border border-stripe-border mb-6 text-sm text-stripe-slate text-center">
                  <span className="font-semibold text-stripe-dark">{loan.userName}</span> has named you as a guarantor for a loan of{' '}
                  <span className="font-semibold text-stripe-blue">LKR {loan.amountRequested?.toLocaleString()}</span>.
                </div>
              )}

              {error && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl flex gap-3 mb-5">
                  <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs font-medium text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={otpSent ? handleOTPVerify : handleSendOTP} className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-stripe-slate uppercase tracking-widest mb-1.5 block">Mobile Number</label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                    <input
                      type="tel"
                      required
                      className="w-full bg-stripe-bg border border-stripe-border rounded-xl py-3.5 pl-11 pr-4 text-sm font-bold text-stripe-dark focus:ring-4 focus:ring-stripe-blue/10 focus:border-stripe-blue transition-all"
                      placeholder="07X XXX XXXX"
                      value={phone}
                      onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                      disabled={isVerifying || otpSent}
                    />
                  </div>
                </div>

                {otpSent && (
                  <div className="animate-in slide-in-from-top-4 duration-300">
                    <label className="text-[10px] font-bold text-stripe-slate uppercase tracking-widest mb-1.5 block">Enter 6-digit OTP</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                      <input
                        type="text"
                        className="w-full bg-stripe-bg border border-stripe-border rounded-xl py-3.5 pl-11 pr-4 text-sm font-bold text-stripe-dark focus:ring-4 focus:ring-stripe-blue/10 focus:border-stripe-blue transition-all"
                        placeholder="XXXXXX"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        disabled={isVerifying}
                        required
                      />
                    </div>
                    <div className="mt-4 text-center">
                       <button 
                         type="button" 
                         onClick={() => setOtpSent(false)} 
                         className="text-[10px] text-stripe-blue font-bold tracking-widest uppercase hover:underline"
                       >
                         Change number or resend
                       </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isVerifying}
                  className="w-full py-4 bg-stripe-dark text-white rounded-xl font-bold text-sm shadow-sm hover:shadow-primary-500/20 active:scale-[0.98] transition-all disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <><Loader2 size={16} className="animate-spin" /> {otpSent ? 'Confirming code...' : 'Sending code...'}</>
                  ) : (
                    otpSent ? <><ShieldCheck size={18} /> Verify Identity</> : <><Send size={18} /> Send Verification Code</>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Document upload */}
          {step === 2 && (
            <div className="s-card p-6 animate-in fade-in duration-300">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-stripe-dark">Upload Documents</h2>
                <p className="text-sm text-stripe-slate mt-1">Please provide the required documents for verification.</p>
              </div>

              <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
                {/* NIC */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Camera size={15} className="text-stripe-blue" />
                    <h4 className="text-xs font-bold text-stripe-dark uppercase tracking-wider">NIC documents</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {['nicFront', 'nicBack'].map(key => (
                      <div key={key}>
                        <p className="text-[10px] font-semibold text-stripe-slate uppercase tracking-wider text-center mb-2">
                          {key === 'nicFront' ? 'NIC Front' : 'NIC Back'}
                        </p>
                        <div className={`relative h-28 rounded-xl overflow-hidden border-2 border-dashed transition-all cursor-pointer ${
                          files[key] ? 'border-primary-400 bg-primary-50' : 'border-stripe-border hover:border-primary-300 bg-stripe-bg'
                        }`}>
                          <input type="file" className="absolute inset-0 opacity-0 z-10 cursor-pointer" accept="image/*"
                            onChange={(e) => setFiles({ ...files, [key]: e.target.files[0] })} />
                          {files[key] ? (
                            <img src={URL.createObjectURL(files[key])} alt="preview" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
                              <Upload size={18} className="text-stripe-light" />
                              <span className="text-[10px] font-medium text-stripe-light">Click to upload</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Financial Verification (Payslips) */}
                <div className="border-t border-stripe-border pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen size={15} className="text-stripe-blue" />
                    <h4 className="text-xs font-bold text-stripe-dark uppercase tracking-wider">Financial Proof (Payslips)</h4>
                  </div>
                  <div className={`relative h-20 rounded-xl overflow-hidden border-2 border-dashed cursor-pointer transition-all ${
                    files.bankStatement ? 'border-green-400 bg-green-50' : 'border-stripe-border hover:border-stripe-blue bg-stripe-bg'
                  }`}>
                    <input type="file" className="absolute inset-0 opacity-0 z-10 cursor-pointer" accept="application/pdf,image/*"
                      onChange={(e) => setFiles({ ...files, bankStatement: e.target.files[0] })} />
                    <div className="w-full h-full flex items-center gap-3 px-6">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${files.bankStatement ? 'bg-green-100 text-green-600' : 'bg-white text-stripe-slate shadow-sm'}`}>
                         {files.bankStatement ? <FileCheck size={20} /> : <Upload size={20} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest mb-0.5">Attach latest Payslips</p>
                        <p className="text-xs font-black text-stripe-dark truncate uppercase">
                          {files.bankStatement ? files.bankStatement.name : 'Select JPG or PDF'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Signature */}
                <div className="border-t border-stripe-border pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <FileCheck size={15} className="text-stripe-blue" />
                    <h4 className="text-xs font-bold text-stripe-dark uppercase tracking-wider">Electronic signature</h4>
                  </div>
                  <div className="bg-stripe-bg rounded-xl border border-stripe-border p-4">
                    <SignaturePad onSave={(data) => setFiles({ ...files, signature: data })} clearOnSave={false} />
                    <p className="text-[10px] text-stripe-light text-center mt-3 font-medium">
                      By signing, you agree to act as a legal guarantor for this loan.
                    </p>
                  </div>
                </div>
              </div>

              <button
                disabled={loading}
                onClick={handleFinalSubmit}
                className="btn-primary w-full py-3 text-sm mt-5 disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {loading ? 'Uploading…' : 'Complete verification'}
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="s-card p-10 text-center animate-in zoom-in duration-300">
              <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-stripe">
                <CheckCircle2 size={32} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-stripe-dark mb-2">Verification Complete</h2>
              <p className="text-sm text-stripe-slate leading-relaxed max-w-xs mx-auto mb-6">
                Your identity and endorsement have been successfully verified. You may close this window.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-stripe-bg border border-stripe-border rounded-xl text-xs font-semibold text-stripe-slate">
                <Lock size={11} />
                Reference: {loanId.substring(0, 8).toUpperCase()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
